<template>
  <div>
    <!-- Search -->
    <div class="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 mb-2">
      <i class="pi pi-search text-gray-400 text-sm" />
      <input
        v-model="query"
        placeholder="חיפוש שירות"
        class="flex-1 bg-transparent outline-none text-[13px]"
      />
    </div>

    <!-- Category chips -->
    <div v-if="categories.length > 1" class="flex gap-1.5 overflow-x-auto pb-1 mb-3">
      <button
        v-for="c in categories"
        :key="c"
        type="button"
        class="px-3 py-1 rounded-full text-xs whitespace-nowrap border transition-colors"
        :class="cat === c ? 'bg-primary text-white border-transparent font-medium' : 'bg-white text-gray-600 border-gray-200'"
        @click="cat = c"
      >{{ c === ALL ? 'הכול' : c }}</button>
    </div>

    <!-- Select all / clear (acts on the current filter) -->
    <div v-if="filtered.length" class="flex items-center justify-end mb-2">
      <button
        type="button"
        class="text-[12px] font-medium text-primary flex items-center gap-1.5"
        @click="toggleSelectAll"
      >
        <i :class="['pi !text-xs', allFilteredSelected ? 'pi-times-circle' : 'pi-check-circle']" />
        {{ allFilteredSelected ? 'ניקוי הבחירה' : 'בחירת הכל' }}
      </button>
    </div>

    <!-- Service cards -->
    <div class="flex flex-col gap-2">
      <div
        v-for="s in filtered"
        :key="s.id"
        class="flex gap-3 py-2.5 px-4 rounded-lg cursor-pointer transition-colors border"
        :class="isSelected(s.id) ? 'border-primary bg-primary-50/60' : 'border-gray-200 bg-white'"
        @click="emit('toggle', s.id)"
      >
        <img
          v-if="s.imageUrl"
          :src="s.imageUrl"
          class="w-14 h-14 rounded-lg object-cover flex-none"
          alt=""
        />
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-1.5 flex-wrap mb-0.5">
            <span class="text-[13.5px] font-semibold">{{ s.name }}</span>
            <span
              v-if="s.category"
              class="text-[9.5px] text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded-full"
            >{{ s.category }}</span>
          </div>
          <div v-if="s.description" class="text-[11px] text-gray-500 leading-snug mb-1 line-clamp-2">{{ s.description }}</div>
          <div class="flex gap-3.5 text-[11px] items-center">
            <span class="text-gray-500">אורך <b class="text-gray-800 font-semibold">{{ memberDuration(s) }} דק'</b></span>
            <span class="text-gray-500">מחיר <b class="text-gray-800 font-semibold">₪{{ memberPrice(s) }}</b></span>
            <span v-if="hasOverride(s)" class="text-[9px] text-primary-700 bg-primary-100 px-1.5 py-0.5 rounded-full">מותאם</span>
          </div>

          <!-- Per-employee price/duration override (managers only — it changes what the customer pays) -->
          <div v-if="isSelected(s.id) && canManage" class="mt-1.5" @click.stop>
            <button
              v-if="expandedOverride !== s.id"
              type="button"
              class="text-[11px] font-medium text-primary flex items-center gap-1 mt-2"
              @click="openOverride(s)"
            >
              <i class="pi pi-pencil !text-[10px]" /> {{ hasOverride(s) ? 'עריכת התאמה אישית' : 'התאמת מחיר/אורך לעובד זה' }}
            </button>
            <div v-else class="mt-1 flex flex-col gap-2 bg-gray-50 border border-gray-200 rounded-lg p-2.5">
              <div class="flex gap-2">
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-[10px] text-gray-500 mb-0.5">מחיר לעובד</span>
                  <input-number v-model="overrideDraft.price" :placeholder="`₪${s.price}`" :min="0" :maxFractionDigits="2" locale="he-IL" fluid class="w-full" inputClass="w-full !py-1 !text-[13px]" />
                </div>
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-[10px] text-gray-500 mb-0.5">אורך (דק')</span>
                  <input-number v-model="overrideDraft.duration" :placeholder="String(s.duration)" :min="0" :step="5" locale="he-IL" fluid class="w-full" inputClass="w-full !py-1 !text-[13px]" />
                </div>
              </div>
              <div class="flex items-center gap-1.5">
                <Button label="שמירה" size="small" :loading="savingOverride === s.id" @click="saveOverride(s)" />
                <Button v-if="hasOverride(s)" label="איפוס" size="small" severity="secondary" text @click="clearOverride(s)" />
                <Button label="ביטול" size="small" text @click="expandedOverride = null" />
              </div>
            </div>
          </div>
        </div>
        <div class="flex-none self-center">
          <div
            v-if="isSelected(s.id)"
            class="w-[22px] h-[22px] rounded-full bg-primary flex items-center justify-center"
          ><i class="pi pi-check text-white !text-[10px]" /></div>
          <div v-else class="w-[22px] h-[22px] rounded-full border-2 border-gray-300" />
        </div>
      </div>
      <div v-if="!filtered.length" class="text-center text-[12.5px] text-gray-400 py-6">לא נמצאו שירותים</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useAdminStore } from '@app/stores';
import { useToast } from 'vue-toastification';
import { ServicesApi } from '@app/api/admin';
import { providerOverrideFor, resolveServiceDuration, resolveServicePrice } from '@torim-app/utils';
import type { IService } from '@torim-app/types';

const props = withDefaults(defineProps<{
  member: { userId: string; fullName: string };
  selected: string[];
  // Employees pick which services they provide, but not their price / duration.
  canManage?: boolean;
}>(), { canManage: true });
const emit = defineEmits<{ toggle: [id: string]; setSelected: [ids: string[]] }>();

const adminStore = useAdminStore();
const toast = useToast();
const ALL = '__all__';

// Per-employee override editor state
const expandedOverride = ref<string | null>(null);
const overrideDraft = ref<{ price?: number; duration?: number }>({});
const savingOverride = ref<string | null>(null);

const memberPrice = (s: IService) => resolveServicePrice(s, props.member.userId);
const memberDuration = (s: IService) => resolveServiceDuration(s, props.member.userId);
const hasOverride = (s: IService) => !!providerOverrideFor(s, props.member.userId);

const openOverride = (s: IService) => {
  const ov = providerOverrideFor(s, props.member.userId);
  overrideDraft.value = { price: ov?.price, duration: ov?.duration };
  expandedOverride.value = s.id;
};

const saveOverride = async (s: IService) => {
  savingOverride.value = s.id;
  try {
    await ServicesApi.setProviderOverride({
      serviceId: s.id,
      providerId: props.member.userId,
      price: overrideDraft.value.price ?? undefined,
      duration: overrideDraft.value.duration ?? undefined
    });
    await adminStore.fetchBusinessData();
    toast.success('ההתאמה נשמרה');
    expandedOverride.value = null;
  } catch {
    toast.error('שמירת ההתאמה נכשלה');
  } finally {
    savingOverride.value = null;
  }
};

const clearOverride = async (s: IService) => {
  savingOverride.value = s.id;
  try {
    await ServicesApi.setProviderOverride({ serviceId: s.id, providerId: props.member.userId });
    await adminStore.fetchBusinessData();
    toast.success('ההתאמה הוסרה');
    expandedOverride.value = null;
  } catch {
    toast.error('הסרת ההתאמה נכשלה');
  } finally {
    savingOverride.value = null;
  }
};

const query = ref('');
const cat = ref(ALL);

const firstName = computed(() => (props.member.fullName || '').split(' ')[0]);
const services = computed(() => adminStore.getRegularServices);
const categories = computed(() => [ALL, ...Array.from(new Set(services.value.map(s => s.category).filter(Boolean) as string[]))]);

const isSelected = (id: string) => props.selected.includes(id);

const filtered = computed(() => {
  const q = query.value.trim();
  return services.value.filter(s =>
    (cat.value === ALL || s.category === cat.value)
    && (q === '' || s.name.includes(q) || (s.description ?? '').includes(q))
  );
});

// Select-all toggles the currently filtered services (respects search/category); it merges
// into / removes from the parent's full selection so services outside the filter are untouched.
const filteredIds = computed(() => filtered.value.map(s => s.id));
const allFilteredSelected = computed(() =>
  filteredIds.value.length > 0 && filteredIds.value.every(id => props.selected.includes(id))
);
const toggleSelectAll = () => {
  emit('setSelected', allFilteredSelected.value
    ? props.selected.filter(id => !filteredIds.value.includes(id))
    : Array.from(new Set([...props.selected, ...filteredIds.value]))
  );
};
</script>
